import { MenuItem } from '@affine/component/ui/menu';
import { AuthService, DefaultServerService } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { ServerFeature } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { ArrowUpBigIcon, ImportIcon, PlusIcon } from '@blocksuite/icons/rc';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useWorkspaceCreationQuota } from '../../../hooks/use-workspace-creation-quota';
import * as styles from './index.css';

export const AddWorkspace = ({
  onAddWorkspace,
  onNewWorkspace,
}: {
  onAddWorkspace?: () => void;
  onNewWorkspace?: () => void;
}) => {
  const t = useI18n();
  const navigate = useNavigate();
  const defaultServerService = useService(DefaultServerService);
  const workspaceDialogService = useServiceOptional(WorkspaceDialogService);
  const session = useLiveData(useService(AuthService).session.session$);
  const isAuthenticated = session.status === 'authenticated';
  const enableLocalWorkspace = useLiveData(
    defaultServerService.server.config$.selector(
      c =>
        c.features.includes(ServerFeature.LocalWorkspace) ||
        BUILD_CONFIG.isNative
    )
  );

  const quota = useWorkspaceCreationQuota();
  const limitReached = !quota.loading && !quota.canCreate;

  const handleUpgrade = useCallback(() => {
    if (!isAuthenticated || !workspaceDialogService) {
      navigate('/pricing');
      return;
    }
    workspaceDialogService.open('setting', { activeTab: 'workspace:plans' });
  }, [isAuthenticated, navigate, workspaceDialogService]);

  // Single-workspace plan with no upgrade path — hide entire footer
  const isElectron = BUILD_CONFIG.isElectron;
  if (
    limitReached &&
    quota.limit === 1 &&
    !quota.upgradeAvailable &&
    !isElectron
  ) {
    return null;
  }

  return (
    <>
      {isElectron && (
        <MenuItem
          block={true}
          prefixIcon={<ImportIcon />}
          prefixIconClassName={styles.prefixIcon}
          onClick={onAddWorkspace}
          data-testid="add-workspace"
          className={styles.ItemContainer}
        >
          <div className={styles.ItemText}>
            {t['com.streamlain.workspace.local.import']()}
          </div>
        </MenuItem>
      )}
      {limitReached ? (
        <>
          <MenuItem
            block={true}
            prefixIcon={<PlusIcon />}
            prefixIconClassName={styles.prefixIcon}
            disabled
            data-testid="new-workspace"
            className={styles.ItemContainer}
          >
            <div className={styles.ItemText}>
              {t['com.streamlain.workspaceList.addWorkspace.limit-reached']({
                count: String(quota.ownedCount),
                limit: String(quota.limit),
              })}
            </div>
          </MenuItem>
          {quota.upgradeAvailable && (
            <MenuItem
              block={true}
              prefixIcon={<ArrowUpBigIcon />}
              prefixIconClassName={styles.upgradeIcon}
              onClick={handleUpgrade}
              data-testid="upgrade-workspace-limit"
              className={styles.ItemContainer}
            >
              <div className={styles.upgradeText}>
                {!isAuthenticated
                  ? t[
                      'com.streamlain.workspaceList.addWorkspace.sign-in-for-more'
                    ]()
                  : t['com.streamlain.workspaceList.addWorkspace.upgrade']({
                      plan: quota.plan,
                    })}
              </div>
            </MenuItem>
          )}
        </>
      ) : (
        <MenuItem
          block={true}
          prefixIcon={<PlusIcon />}
          prefixIconClassName={styles.prefixIcon}
          onClick={onNewWorkspace}
          data-testid="new-workspace"
          className={styles.ItemContainer}
        >
          <div className={styles.ItemText}>
            {enableLocalWorkspace
              ? t['com.streamlain.workspaceList.addWorkspace.create']()
              : t['com.streamlain.workspaceList.addWorkspace.create-cloud']()}
          </div>
        </MenuItem>
      )}
    </>
  );
};
